'use client'
import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  Lock,
  ShieldCheck,
  User as UserIcon,
  MapPin,
  CreditCard,
  Loader2,
  Users as UsersIcon,
  History,
  Plus,
  ArrowRight,
  Pencil,
  HeartPulse,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { useSignIdentityCard, useAppendIdentityAddendum } from '@/hooks/usePatients'
import { useActivePatient } from '@/context/ActivePatientContext'
import { useClinicFormat } from '@/context/ClinicTimezoneContext'
import { getInitials, calculateAge } from '@/lib/utils'
import type { Patient } from '@/types'

// ── Spanish label maps (NOM-004 ficha de identificación) ─────────────────
const genderLabels: Record<string, string> = {
  male: 'Masculino',
  female: 'Femenino',
  non_binary: 'No binario',
  prefer_not_to_say: 'Prefiere no especificar',
  other: 'Otro',
}

const maritalLabels: Record<string, string> = {
  single: 'Soltero/a',
  married: 'Casado/a',
  divorced: 'Divorciado/a',
  widowed: 'Viudo/a',
  separated: 'Separado/a',
  partnered: 'Unión libre',
}

const coverageLabels: Record<string, string> = {
  private: 'Particular (privado)',
  insurance: 'Aseguranza / seguro de gastos médicos',
  institutional: 'Institucional (IMSS / ISSSTE / etc.)',
}

const languageLabels: Record<string, string> = {
  es: 'Español',
  en: 'Inglés',
}

const NA = 'No especificado'

// Empty-field indicator for the identification grid. A field that carries a
// value renders as plain text; an unset field renders this in a muted/italic
// style (see `Stat`).
const PENDING = 'Pendiente'

function label(map: Record<string, string>, key?: string | null): string {
  if (!key) return NA
  return map[key] ?? key
}

/** Normalize a stored value to a display string, or `null` when it is empty. */
function v(value?: string | number | null): string | null {
  if (value == null) return null
  const s = String(value).trim()
  return s === '' ? null : s
}

/** Resolve an enum code to its Spanish label, or `null` when unset. */
function optionLabel(map: Record<string, string>, key?: string | null): string | null {
  if (!key) return null
  return map[key] ?? key
}

// Identity fields that may need a post-signing correction. After the card is
// signed these are read-only; a clarifying addendum is recorded instead of an
// edit. `format` maps stored codes (enums) to their Spanish display value.
const CORRECTABLE_FIELDS: Array<{
  field: keyof Patient
  label: string
  format?: (v: unknown) => string
}> = [
  { field: 'phone_primary', label: 'Teléfono principal' },
  { field: 'phone_secondary', label: 'Teléfono secundario' },
  { field: 'email', label: 'Correo electrónico' },
  { field: 'address_line1', label: 'Domicilio · calle y número' },
  { field: 'address_line2', label: 'Domicilio · colonia / interior' },
  { field: 'city', label: 'Ciudad' },
  { field: 'state', label: 'Estado' },
  { field: 'postal_code', label: 'Código postal' },
  { field: 'marital_status', label: 'Estado civil', format: (v) => label(maritalLabels, v as string) },
  { field: 'occupation', label: 'Ocupación' },
  { field: 'religion', label: 'Religión' },
  { field: 'education_level', label: 'Escolaridad' },
  { field: 'coverage_type', label: 'Tipo de cobertura', format: (v) => label(coverageLabels, v as string) },
  { field: 'referring_physician', label: 'Médico que refiere' },
  { field: 'guardian_name', label: 'Representante legal · nombre' },
  { field: 'guardian_relationship', label: 'Representante legal · parentesco' },
  { field: 'guardian_phone', label: 'Representante legal · teléfono' },
  { field: 'guardian_id_document', label: 'Representante legal · identificación' },
]

interface PatientIdentificationCardProps {
  patient: Patient
  /** When true (the Patient Data section), render the "Agregar aclaración"
   *  action that lets a signed card receive a NOM-004 clarifying addendum.
   *  Left off in read-only contexts such as the Resumen tab. */
  showAddendumAction?: boolean
}

export function PatientIdentificationCard({
  patient,
  showAddendumAction = false,
}: PatientIdentificationCardProps) {
  const { formatDate, formatDateTime } = useClinicFormat()
  const { setActivePatient } = useActivePatient()
  const { toast } = useToast()
  const { mutate: signCard, isPending } = useSignIdentityCard()
  const { mutate: addAddendum, isPending: isAddingAddendum } = useAppendIdentityAddendum()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [addendumOpen, setAddendumOpen] = useState(false)
  const [addendumField, setAddendumField] = useState<string>(CORRECTABLE_FIELDS[0].field as string)
  const [addendumValue, setAddendumValue] = useState('')
  const [addendumNote, setAddendumNote] = useState('')

  const isSigned = !!patient.identity_signed_at

  // Group 4 (legal guardian) collapses to a single dimmed line unless the
  // patient is a dependent/minor or already has a guardian on file. The toggle
  // is a local view affordance — the underlying ficha is read-only once signed.
  const age = patient.date_of_birth ? calculateAge(patient.date_of_birth) : patient.age
  const isMinor = typeof age === 'number' && age < 18
  const hasGuardianData =
    patient.is_dependent || isMinor || !!patient.guardian_name
  const [guardianMode, setGuardianMode] = useState<'na' | 'dependent'>(
    hasGuardianData ? 'dependent' : 'na',
  )

  const addenda = patient.identity_addenda ?? []

  // The "current effective value" of a field is the latest addendum's new value
  // (corrections chain), falling back to the original signed value.
  const effectiveValue = useMemo(
    () =>
      (field: string): string => {
        const last = [...addenda].reverse().find((a) => a.field === field)
        if (last) return last.new_value
        const cfg = CORRECTABLE_FIELDS.find((f) => f.field === field)
        const raw = (patient as unknown as Record<string, unknown>)[field]
        if (raw == null || raw === '') return ''
        return cfg?.format ? cfg.format(raw) : String(raw)
      },
    [addenda, patient],
  )

  const allergies = (patient.allergies ?? []).filter((a) => a?.name)
  const primaryContact =
    patient.emergency_contacts?.find((c) => c.is_primary) ?? patient.emergency_contacts?.[0]
  const primaryInsurance =
    patient.insurance_info?.find((i) => i.is_primary) ?? patient.insurance_info?.[0]

  function handleSign() {
    signCard(patient.id, {
      onSuccess: (updated) => {
        setActivePatient(updated)
        setConfirmOpen(false)
        toast({
          title: 'Ficha firmada y cerrada',
          description: 'La ficha de identificación quedó bloqueada (solo lectura).',
        })
      },
      onError: () => {
        toast({
          title: 'No se pudo firmar',
          description: 'Ocurrió un error al cerrar la ficha de identificación.',
          variant: 'destructive',
        })
      },
    })
  }

  function openAddendum() {
    setAddendumField(CORRECTABLE_FIELDS[0].field as string)
    setAddendumValue('')
    setAddendumNote('')
    setAddendumOpen(true)
  }

  function handleAddendum() {
    const value = addendumValue.trim()
    if (!value) return
    const cfg = CORRECTABLE_FIELDS.find((f) => f.field === addendumField)
    addAddendum(
      {
        patientId: patient.id,
        payload: {
          field: addendumField,
          field_label: cfg?.label ?? addendumField,
          previous_value: effectiveValue(addendumField) || null,
          new_value: value,
          note: addendumNote.trim() || null,
        },
      },
      {
        onSuccess: (updated) => {
          setActivePatient(updated)
          setAddendumOpen(false)
          toast({
            title: 'Aclaración registrada',
            description: 'La corrección quedó asentada sin alterar el valor original.',
          })
        },
        onError: () => {
          toast({
            title: 'No se pudo registrar la aclaración',
            description: 'Ocurrió un error al asentar la nota de aclaración.',
            variant: 'destructive',
          })
        },
      },
    )
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-ml-outline-variant/15 bg-ml-surface-container-lowest shadow-sm">
      {/* ── Anchor block: who the patient is (name · record # · avatar). The
          single most prominent element of the card. Signed/lock state sits in
          the top-right corner. ─────────────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-4 border-b border-ml-outline-variant/15 bg-gradient-to-br from-ml-surface-container-low to-ml-surface-container-lowest px-5 py-5">
        <div className="flex min-w-0 items-center gap-4">
          {patient.photo_url ? (
            <img
              src={patient.photo_url}
              alt={patient.full_name}
              className="h-16 w-16 shrink-0 rounded-2xl object-cover ring-1 ring-ml-outline-variant/20"
            />
          ) : (
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-ml-primary/10 text-xl font-bold text-ml-primary">
              {getInitials(patient.first_name, patient.last_name)}
            </span>
          )}
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-ml-primary/80">
              Ficha de identificación · NOM-004
            </p>
            <h3 className="truncate text-xl font-bold text-ml-on-surface">{patient.full_name}</h3>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-ml-on-surface-variant">
              <span className="font-mono font-semibold text-ml-on-surface">Exp. {patient.mrn}</span>
              {age != null && <span>· {age} años</span>}
              <span>· {label(genderLabels, patient.gender)}</span>
            </p>
          </div>
        </div>
        <div className="shrink-0">
          {isSigned ? (
            <span className="inline-flex items-center gap-1 rounded bg-teal-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-teal-800">
              <Lock className="h-3 w-3" /> Cerrada y firmada
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-800">
              Abierta · sin firmar
            </span>
          )}
        </div>
      </header>

      {/* ── Allergy alert: directly below the anchor, before any other data,
          impossible to miss (red, with a strong left accent). ──────────────── */}
      {allergies.length > 0 && (
        <div className="flex items-start gap-3 border-b border-ml-error/20 border-l-4 border-l-ml-error bg-ml-error/10 px-5 py-3.5">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-ml-error" />
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest text-ml-error">
              Alergias conocidas
            </p>
            <ul className="mt-1 space-y-0.5">
              {allergies.map((a, i) => (
                <li key={`${a.name}-${i}`} className="text-sm text-ml-on-surface">
                  <span className="font-bold">{a.name}</span>
                  {a.reaction ? ` — ${a.reaction}` : ''}
                  {a.severity ? <span className="text-ml-error/80"> ({a.severity})</span> : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ── Body: four identification cards (general · contact · insurance ·
          guardian), each a distinct card surface. ─────────────────────────── */}
      <div className="space-y-6 px-5 py-5">
        {/* ── Card 1 · General information — three divider-separated sub-groups */}
        <SectionCard icon={UserIcon} title="Información general">
          {/* Identity: the three name parts on a single row */}
          <SubGroup first>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
              <Stat label="Nombre(s)" value={v(patient.first_name)} />
              <Stat label="Segundo nombre" value={v(patient.middle_name)} />
              <Stat label="Apellido(s)" value={v(patient.last_name)} />
            </dl>
          </SubGroup>

          {/* Birth & demographics — 3-column grid */}
          <SubGroup title="Nacimiento y datos demográficos">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
              <Stat
                label="Fecha de nacimiento"
                value={
                  patient.date_of_birth
                    ? `${formatDate(patient.date_of_birth)}${age != null ? ` · ${age} años` : ''}`
                    : null
                }
              />
              <Stat label="Sexo" value={optionLabel(genderLabels, patient.gender)} />
              <Stat label="CURP" value={v(patient.curp)} mono />
              <Stat label="Lugar de nacimiento" value={v(patient.place_of_birth)} />
              <Stat label="Nacionalidad" value={v(patient.nationality)} />
              <Stat
                label="Estado civil"
                value={
                  isMinor ? 'No aplica — menor de edad' : optionLabel(maritalLabels, patient.marital_status)
                }
              />
            </dl>
          </SubGroup>

          {/* Profile — 3-column grid */}
          <SubGroup title="Perfil">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
              <Stat label="Escolaridad" value={v(patient.education_level)} />
              <Stat label="Ocupación" value={v(patient.occupation)} />
              <Stat label="Religión" value={v(patient.religion)} />
              <Stat
                label="Lengua materna"
                value={v(patient.native_language) ?? optionLabel(languageLabels, patient.preferred_language)}
              />
              <Stat label="Grupo sanguíneo" value={v(patient.blood_type)} />
            </dl>
          </SubGroup>
        </SectionCard>

        {/* ── Card 2 · Contact and address */}
        <SectionCard icon={MapPin} title="Contacto y domicilio">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <Stat label="Teléfono principal" value={v(patient.phone_primary)} />
            <Stat label="Teléfono secundario" value={v(patient.phone_secondary)} />
            <Stat label="Correo electrónico" value={v(patient.email)} className="sm:col-span-2" />
          </dl>

          {/* Address — highlighted sub-box for quick reference */}
          <div className="mt-4 rounded-xl border border-ml-outline-variant/20 bg-ml-surface-container-high/50 p-3.5">
            <p className="mb-2.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ml-on-surface-variant/70">
              <MapPin className="h-3 w-3" /> Domicilio
            </p>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              <Stat label="Calle y número" value={v(patient.address_line1)} />
              <Stat label="Colonia / interior" value={v(patient.address_line2)} />
            </dl>
            <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              <Stat label="Ciudad" value={v(patient.city)} />
              <Stat label="Estado" value={v(patient.state)} />
              <Stat label="Código postal" value={v(patient.postal_code)} mono />
              <Stat label="País" value={v(patient.country)} />
            </dl>
          </div>

          {/* Emergency contact — highlighted line with an amend action */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl border-l-4 border-l-ml-primary bg-ml-primary/5 px-3.5 py-3">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ml-primary/80">
                <HeartPulse className="h-3 w-3" /> Contacto de emergencia
              </p>
              <p
                className={[
                  'mt-0.5 text-sm',
                  primaryContact ? 'font-medium text-ml-on-surface' : 'italic text-ml-on-surface-variant/50',
                ].join(' ')}
              >
                {primaryContact
                  ? [primaryContact.name, primaryContact.relationship || 'Sin parentesco', primaryContact.phone]
                      .filter(Boolean)
                      .join(' · ')
                  : PENDING}
              </p>
            </div>
            {showAddendumAction && isSigned && (
              <button
                type="button"
                onClick={openAddendum}
                className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-ml-primary hover:underline"
              >
                <Pencil className="h-3 w-3" /> Editar o agregar
              </button>
            )}
          </div>
        </SectionCard>

        {/* ── Card 3 · Insurance and referral — 2-column grid */}
        <SectionCard icon={CreditCard} title="Aseguramiento y referencia">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <Stat label="Tipo de cobertura" value={optionLabel(coverageLabels, patient.coverage_type)} />
            <Stat label="Médico que refiere" value={v(patient.referring_physician)} />
            <Stat label="Aseguradora" value={v(primaryInsurance?.provider)} />
            <Stat label="Número de póliza" value={v(primaryInsurance?.policy_number)} mono />
          </dl>
        </SectionCard>

        {/* ── Card 4 · Legal guardian / representative — collapsible */}
        <SectionCard
          icon={UsersIcon}
          title="Representante legal / tutor"
          action={
            <div className="inline-flex rounded border border-ml-outline-variant/30 bg-ml-surface p-0.5 text-[11px] font-semibold">
              {(['na', 'dependent'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setGuardianMode(m)}
                  className={[
                    'rounded-sm px-3 py-1 transition',
                    guardianMode === m
                      ? 'bg-ml-primary text-ml-on-primary'
                      : 'text-ml-on-surface-variant/70 hover:text-ml-on-surface',
                  ].join(' ')}
                >
                  {m === 'na' ? 'No aplica' : 'Dependiente'}
                </button>
              ))}
            </div>
          }
        >
          {guardianMode === 'dependent' ? (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
              <Stat label="Nombre completo" value={v(patient.guardian_name)} />
              <Stat label="Parentesco" value={v(patient.guardian_relationship)} />
              <Stat label="Teléfono" value={v(patient.guardian_phone)} />
              <Stat label="Identificación oficial" value={v(patient.guardian_id_document)} mono />
            </dl>
          ) : (
            <p className="text-sm italic text-ml-on-surface-variant/50">
              No aplica — el paciente no tiene tutor o representante legal registrado.
            </p>
          )}
        </SectionCard>
      </div>

      {/* ── Clarifying addenda (NOM-004 "notas de aclaración") ──────────── */}
      {(addenda.length > 0 || (showAddendumAction && isSigned)) && (
        <div className="border-t border-ml-outline-variant/15 px-5 py-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-ml-primary" />
              <h4 className="text-xs font-bold uppercase tracking-widest text-ml-primary">
                Aclaraciones y correcciones
              </h4>
            </div>
            {showAddendumAction && isSigned && (
              <Button variant="outline" size="sm" onClick={openAddendum}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Agregar aclaración
              </Button>
            )}
          </div>

          {addenda.length === 0 ? (
            <p className="text-xs italic text-ml-on-surface-variant/60">
              Sin aclaraciones. El valor original firmado se conserva; cualquier corrección se
              registra aquí sin sobrescribirlo.
            </p>
          ) : (
            <ol className="space-y-3">
              {[...addenda]
                .map((a, i) => ({ a, i }))
                .reverse()
                .map(({ a, i }) => (
                  <li
                    key={`${a.field}-${a.date}-${i}`}
                    className="rounded-xl border border-ml-outline-variant/15 bg-ml-surface p-3"
                  >
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-xs font-bold uppercase tracking-wider text-ml-on-surface">
                        {a.field_label || a.field}
                      </span>
                      <span className="text-[11px] text-ml-on-surface-variant/70">
                        · {a.author_name} · {formatDateTime(a.date)}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm">
                      <span className="rounded bg-ml-error/10 px-2 py-0.5 text-ml-on-surface-variant line-through">
                        {a.previous_value || '—'}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 text-ml-on-surface-variant/50" />
                      <span className="rounded bg-teal-100 px-2 py-0.5 font-medium text-teal-900">
                        {a.new_value}
                      </span>
                    </div>
                    {a.note && (
                      <p className="mt-1.5 text-xs text-ml-on-surface-variant/80">{a.note}</p>
                    )}
                  </li>
                ))}
            </ol>
          )}
        </div>
      )}

      {/* ── Footer: opening clinician + license + lock indicator ──────── */}
      <footer className="border-t border-ml-outline-variant/15 bg-ml-surface-container-low px-5 py-4">
        {isSigned ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" />
              <div className="text-xs">
                <p className="font-semibold text-ml-on-surface">
                  Expediente abierto y firmado por {patient.identity_signed_by_name || 'médico'}
                </p>
                <p className="text-ml-on-surface-variant/80">
                  Céd. prof. {patient.identity_signed_by_license || 'no registrada'}
                  {' · '}
                  {patient.identity_signed_at
                    ? formatDateTime(patient.identity_signed_at)
                    : ''}
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded bg-teal-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-teal-800">
              <Lock className="h-3.5 w-3.5" /> Cerrada y firmada · solo lectura
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] leading-relaxed text-ml-on-surface-variant/80">
              Al firmar, la ficha de identificación se cierra y queda{' '}
              <span className="font-semibold">bloqueada y de solo lectura</span> a su nombre y
              cédula profesional. Esta acción no se puede deshacer.
            </p>
            <Button onClick={() => setConfirmOpen(true)} className="shrink-0">
              <Lock className="mr-2 h-4 w-4" /> Firmar y cerrar ficha
            </Button>
          </div>
        )}
      </footer>

      {/* Sign confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Firmar y cerrar la ficha de identificación</DialogTitle>
            <DialogDescription>
              La ficha quedará firmada a su nombre y cédula profesional, con la fecha y hora
              actuales. Una vez cerrada no podrá editarse (solo lectura). ¿Desea continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={isPending}>
              Cancelar
            </Button>
            <Button onClick={handleSign} disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Firmar y cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add clarifying addendum */}
      <Dialog open={addendumOpen} onOpenChange={setAddendumOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar aclaración (NOM-004)</DialogTitle>
            <DialogDescription>
              La ficha está firmada y cerrada. El valor original no se modifica; se asienta una
              nota de aclaración con el valor anterior, el nuevo valor, su autoría y la fecha.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">
                Campo a aclarar
              </label>
              <select
                value={addendumField}
                onChange={(e) => setAddendumField(e.target.value)}
                className="w-full rounded-lg border border-ml-outline-variant/40 bg-ml-surface px-3 py-2 text-sm text-ml-on-surface outline-none focus:border-ml-primary"
              >
                {CORRECTABLE_FIELDS.map((f) => (
                  <option key={f.field as string} value={f.field as string}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">
                Valor actual
              </label>
              <p className="rounded-lg bg-ml-surface-container-high px-3 py-2 text-sm text-ml-on-surface-variant">
                {effectiveValue(addendumField) || '—'}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">
                Nuevo valor
              </label>
              <Textarea
                value={addendumValue}
                onChange={(e) => setAddendumValue(e.target.value)}
                placeholder="Valor corregido o actualizado"
                rows={2}
              />
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">
                Motivo / nota (opcional)
              </label>
              <Textarea
                value={addendumNote}
                onChange={(e) => setAddendumNote(e.target.value)}
                placeholder="Ej. cambio de domicilio reportado por el paciente"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddendumOpen(false)}
              disabled={isAddingAddendum}
            >
              Cancelar
            </Button>
            <Button onClick={handleAddendum} disabled={isAddingAddendum || !addendumValue.trim()}>
              {isAddingAddendum && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Registrar aclaración
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

/**
 * One of the four top-level identification cards: a distinct card surface with a
 * subtle border + shadow, an icon + small-caps header, and an optional header
 * action slot (used by the guardian card's collapse toggle).
 */
function SectionCard({
  icon: Icon,
  title,
  action,
  children,
}: {
  icon: React.ElementType
  title: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="rounded-2xl border border-ml-outline-variant/20 bg-ml-surface p-4 shadow-sm sm:p-5">
      <header className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-ml-primary" />
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ml-on-surface-variant">
            {title}
          </h4>
        </div>
        {action}
      </header>
      {children}
    </section>
  )
}

/**
 * A sub-group inside a card, separated from the previous one by a thin divider
 * and a small caption (not a full nested card). `first` omits the top divider.
 */
function SubGroup({
  title,
  first = false,
  children,
}: {
  title?: string
  first?: boolean
  children: React.ReactNode
}) {
  return (
    <div className={first ? '' : 'mt-4 border-t border-ml-outline-variant/15 pt-4'}>
      {title && (
        <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ml-primary/70">
          {title}
        </p>
      )}
      {children}
    </div>
  )
}

/**
 * A single grid cell: a small muted label above a plain-text value. An unset
 * value (`null`) falls back to "Pendiente" in a muted/italic style.
 */
function Stat({
  label,
  value,
  mono = false,
  className = '',
}: {
  label: string
  value?: string | null
  mono?: boolean
  className?: string
}) {
  const isEmpty = value == null || value === ''
  return (
    <div className={['min-w-0', className].join(' ')}>
      <dt className="text-[10px] font-medium uppercase tracking-wider text-ml-on-surface-variant/60">
        {label}
      </dt>
      <dd
        className={[
          'mt-0.5 break-words text-sm',
          isEmpty ? 'italic text-ml-on-surface-variant/50' : 'font-medium text-ml-on-surface',
          mono && !isEmpty ? 'font-mono' : '',
        ].join(' ')}
      >
        {isEmpty ? PENDING : value}
      </dd>
    </div>
  )
}
